/* @flow */
import React from 'react';
import SimpleImageCarousel from '@homeaway/react-simple-image-carousel';
import {SVGIcon} from '@homeaway/react-svg';
import PaginationDots from '@homeaway/react-pagination-dots';
import {CHEVRON_LEFT__16, CHEVRON_RIGHT__16} from '@homeaway/svg-defs';
import HitLabel from '../../HitLabel/HitLabel';
import {convertImagesIntoSimpleCarouselFormat} from '../../HitCarouselContainer/HitCarouselContainer';
import {
    PHOTO_NEXT,
    PHOTO_PREVIOUS,
    triggerMediaInteractionEdapEvent
} from '../../../utils/carouselEdapUtils';

import './HitCarousel.less';

import type {BadgeShape, ListingImage} from '../Hit';

type Props = {
    detailPageUrl: string,
    edapCollectionType: string,
    images: ListingImage[],
    onImageClick?: Function,
    premierPartnerBadge?: BadgeShape,
    viewedImages: Array<number>,
    setViewedImages: Function
};

const HitCarousel = (props: Props) => {
    const {
        detailPageUrl,
        edapCollectionType,
        images,
        onImageClick,
        premierPartnerBadge,
        viewedImages,
        setViewedImages
    } = props;
    const [imageIndex, setImageIndex] = React.useState(0);
    const carouselImages = convertImagesIntoSimpleCarouselFormat(images);

    const onImageNavigate = (index: number) => {
        const previousIndex = imageIndex;
        const imageSrc = carouselImages[index].src;

        setImageIndex(index);
        if (!viewedImages.includes(index)) {
            setViewedImages([...viewedImages, index]);
        }

        const edapLabel =
            (previousIndex + 1 === carouselImages.length && index === 0) ||
            previousIndex + 1 === index
                ? PHOTO_NEXT
                : PHOTO_PREVIOUS;

        triggerMediaInteractionEdapEvent(
            edapCollectionType,
            edapLabel,
            imageSrc,
            index
        );
    };

    let tooltipParentNode;
    /* istanbul ignore next */
    if (typeof window !== 'undefined') {
        tooltipParentNode = document.getElementsByTagName('body')[0];
    }

    return (
        <div className="HitCarousel">
            {premierPartnerBadge && (
                <div className={'HitCarousel__badge'}>
                    <HitLabel
                        parentNode={tooltipParentNode}
                        label={premierPartnerBadge.name}
                        toolTipText={premierPartnerBadge.helpText}
                    />
                </div>
            )}
            <SimpleImageCarousel
                images={carouselImages}
                index={imageIndex}
                href={detailPageUrl}
                target={'_blank'}
                transitionStyle="slide"
                transitionDuration={250}
                onNextClick={onImageNavigate}
                onPrevClick={onImageNavigate}
                onClick={onImageClick}
                renderButtons={({setPrevRef, setNextRef}) => {
                    return [
                        <button
                            key="0"
                            className="SimpleImageCarousel__button SimpleImageCarousel__button--left"
                            data-wdio="SimpleImageCarousel__button--prev"
                            ref={setPrevRef}
                        >
                            <span className="sr-only">{'Previous Image'}</span>
                            <SVGIcon
                                className="SimpleImageCarousel__svg SimpleImageCarousel__svg--left"
                                markup={CHEVRON_LEFT__16}
                            />
                        </button>,
                        <button
                            key="1"
                            className="SimpleImageCarousel__button SimpleImageCarousel__button--right"
                            data-wdio="SimpleImageCarousel__button--next"
                            ref={setNextRef}
                        >
                            <span className="sr-only">{'Next Image'}</span>
                            <SVGIcon
                                className="SimpleImageCarousel__svg SimpleImageCarousel__svg--right"
                                markup={CHEVRON_RIGHT__16}
                            />
                        </button>
                    ];
                }}
            />
            <PaginationDots
                imageIndex={imageIndex}
                maxImages={carouselImages.length}
            />
        </div>
    );
};

export default HitCarousel;
